/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { FieldGroup, FieldText } from "@adapptio/lib-editor-ui";
import { useBlueprintModel } from "../../../ui/hooks/useBlueprintModel";
import { useEditorInspectorContext } from "../../../ui/contexts/EditorInspectorContext";
import { InvalidModelNode } from "../../../ui/components/Inspector/InvalidModelNode";
import { IInspectorControl_Password, TInspectorControlProps } from "../InspectorControlTypes";
import { isModelValueRequired } from "@adapptio/lib-blueprint";

export interface IInspectorControlPasswordProps extends TInspectorControlProps<IInspectorControl_Password> {}

export const InspectorControlPassword: React.FC<IInspectorControlPasswordProps> = ({ model: _model }) => {
	const { model, validationErrors } = useBlueprintModel(_model, "constPassword");
	const inspectorContext = useEditorInspectorContext();

	const required = isModelValueRequired(model);

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, value, true);
		},
		[ model ]
	);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<FieldGroup grow>
			<FieldText
				type="password"
				value={model.value}
				onChange={handleChange}
				validationErrors={validationErrors}
				grow={true}
				readOnly={inspectorContext?.readOnly}
				debounceTimeout={inspectorContext?.inputDebounceTimeout}
				minLength={model.schema.opts.constraints?.min}
				maxLength={model.schema.opts.constraints?.max}
				clearable={!required}
			/>
		</FieldGroup>
	);
};
